import { type CSSProperties, type KeyboardEvent, useState } from "react";
import { eventNames, shopWindowTypes, valueNames } from "@/modules/Constants";
import {
  isChainAnchoredOnMethodStep,
  isChainRevealed,
} from "@/modules/chainReveal";
import {
  findAskConfig,
  hasSeenPixieTip,
  isPixieOpen,
  markPixieTipSeen,
} from "@/modules/pixieWorkspace";
import { pixieIntentProps } from "@/components/shopwindow/ask/pixieChunk";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

// First-time tooltip pointing at the way into Pixie, until the viewer opens her
// or says Not Now. Placement comes from `placement` (see pixie-workspace.css).
// Draft copy, to be rewritten in Pixie's voice.
type PixieIntroTipProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
  placement?: string;
  style?: CSSProperties;
};

// The Up Next card (and its countdown) sits where the tip would.
function upNextShowing(experienceState: ExperienceState) {
  const applicationData = experienceState.applicationData;
  const config = applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG) as
    | ShopWindowConfig
    | undefined;
  return (
    isChainRevealed(applicationData) ||
    (!!config &&
      config.type === shopWindowTypes.METHOD &&
      isChainAnchoredOnMethodStep(
        applicationData,
        config,
        experienceState.experienceData,
      ))
  );
}

// Placement-specific box; gap, padding and the animation stay in pixie-workspace.css for landscape
const PLACEMENT: Record<string, string> = {
  portrait: "tw:right-[12px] tw:left-[12px]",
  landscape:
    "tw:top-[50%] tw:right-[calc(100%+14px)] tw:w-[280px] tw:[transform:translateY(-50%)]",
};

export default function PixieIntroTip({
  experienceState,
  onApplicationEvent,
  placement,
  style,
}: PixieIntroTipProps) {
  const [dismissed, setDismissed] = useState(() => hasSeenPixieTip());

  const onNotNow = () => {
    markPixieTipSeen();
    setDismissed(true);
  };

  const onMeet = () => {
    const config = findAskConfig(experienceState.applicationData);
    setDismissed(true);
    if (config) {
      onApplicationEvent({
        name: eventNames.SET_SHOP_WINDOW,
        region: "Pixie Tooltip",
        shopWindowConfig: config,
      });
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onNotNow();
    }
  };

  const applicationData = experienceState.applicationData;
  if (
    dismissed ||
    hasSeenPixieTip() ||
    !findAskConfig(applicationData) ||
    isPixieOpen(applicationData) ||
    upNextShowing(experienceState)
  ) {
    return null;
  }
  // Non-modal: it never takes focus or blocks the player, so it is a labelled
  // aside rather than a dialog. Escape from its buttons counts as Not Now.
  return (
    <aside
      className={
        "pixie-tip pixie-tip--" +
        placement +
        ` ${(placement && PLACEMENT[placement]) || ""}` +
        ' tw:pointer-events-auto tw:absolute tw:z-6 tw:box-border tw:flex tw:flex-col tw:gap-[10px] tw:rounded-[14px] tw:p-[14px] tw:[font-family:"DM_Sans","Helvetica","Arial",sans-serif] tw:text-[#fff] tw:[-webkit-font-smoothing:antialiased] tw:[background:#1c1c1c] tw:[box-shadow:0_12px_32px_rgba(0,0,0,0.28)]'
      }
      style={style}
      aria-label="Meet Pixie"
      onKeyDown={onKeyDown}
    >
      <p className='pixie-tip__title tw:m-0 tw:font-[700] tw:[font-family:"Nunito","DM_Sans","Helvetica","Arial",sans-serif] tw:text-[16px]'>
        Psst, I’m Pixie
      </p>
      <p className="pixie-tip__body tw:m-0 tw:text-[13px] tw:text-[rgba(255,255,255,0.86)] tw:leading-[1.5]">
        I know you’re keen to get to the recipe, but I had to say hi. Tap Pixie
        and I’ll help you plan the rest of the meal around it.
      </p>
      <div className="pixie-tip__actions tw:flex tw:justify-end tw:gap-[8px]">
        <button
          type="button"
          className="pixie-tip__btn pixie-tip__btn--ghost tw:rounded-[999px] tw:[font-weight:700]"
          onClick={onNotNow}
        >
          Not Now
        </button>
        <button
          type="button"
          className="pixie-tip__btn pixie-tip__btn--solid tw:rounded-[999px] tw:[font-weight:700]"
          onClick={onMeet}
          {...pixieIntentProps}
        >
          Meet Pixie
        </button>
      </div>
      <span
        className="pixie-tip__arrow tw:absolute tw:h-[12px] tw:w-[12px] tw:[background:#1c1c1c] tw:[transform:rotate(45deg)] tw:[.pixie-tip--desktop_&]:right-[calc(25%-6px)] tw:[.pixie-tip--desktop_&]:bottom-[-6px] tw:[.pixie-tip--landscape_&]:top-[calc(50%-6px)] tw:[.pixie-tip--landscape_&]:right-[-6px] tw:[.pixie-tip--portrait_&]:right-[calc(25%-6px)] tw:[.pixie-tip--portrait_&]:bottom-[-6px]"
        aria-hidden="true"
      />
    </aside>
  );
}
